import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, TrendingUp } from "lucide-react";
import API from "../services/api";
import Sidebar from "../components/Sidebar";
import BottomNav from "../components/BottomNav";
import BannerCarousel from "../components/BannerCarousel";
import TickerMarquee from "../components/TickerMarquee";
import WhatsAppGlyph from "../components/WhatsAppGlyph";
import { getMarketStatusLabel, getMarketWindowForNow, isMarketClosed } from "../utils/marketStatus";
import { initPushNotifications } from "../services/push";
import { buildWhatsAppUrl, consumeDashboardNotice, openExternalLink } from "../utils/appInteractions";
import usePullToRefresh from "../hooks/usePullToRefresh";

function Dashboard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [banners, setBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [balance, setBalance] = useState(0);
  const [settings, setSettings] = useState({});
  const [pageNotice, setPageNotice] = useState("");
  const [chartPopup, setChartPopup] = useState(null);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const normalMarkets = useMemo(
    () => markets
      .filter((m) => (m.market_type || "normal") !== "starline")
      .sort(sortMarketsByOpenTime),
    [markets]
  );
  const displayMarkets = normalMarkets.length ? normalMarkets : fallbackMarkets;
  const displayBanners = Array.isArray(banners) ? banners : [];

  useEffect(() => {
    const nextNotice = consumeDashboardNotice();
    if (!nextNotice) return undefined;
    setPageNotice(nextNotice);
    const timer = window.setTimeout(() => setPageNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchWallet(user.id);
      initPushNotifications(user.id).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveBanner((c) => (c + 1) % Math.max(banners.length || 1, 1));
    }, 3500);
    return () => clearInterval(timer);
  }, [banners.length]);

  const fetchMarkets = useCallback(async () => {
    try {
      const res = await API.get("/markets");
      setMarkets(Array.isArray(res.data) ? res.data.slice().sort(sortMarketsByOpenTime) : []);
    } catch {
      setMarkets([]);
    }
  }, []);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await API.get("/banners");
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBanners([]);
    }
  }, []);

  const fetchWallet = async (userId) => {
    try {
      const res = await API.get(`/wallet/${userId}`);
      setBalance(Number(res.data?.balance || 0));
    } catch {
      setBalance(0);
    }
  };

  const refreshDashboard = useCallback(async () => {
    await Promise.all([
      fetchMarkets(),
      fetchBanners(),
      API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {}),
      user?.id ? fetchWallet(user.id) : Promise.resolve(),
    ]);
  }, [fetchBanners, fetchMarkets, user]);

  useEffect(() => {
    refreshDashboard().catch(() => {});
  }, [refreshDashboard]);

  useEffect(() => {
    const interval = window.setInterval(() => refreshDashboard().catch(() => {}), 30000);
    return () => window.clearInterval(interval);
  }, [refreshDashboard]);

  const pullToRefresh = usePullToRefresh(refreshDashboard);

  const openGameSelect = (market) => {
    if (isMarketClosed(market)) { alert("Market closed for today"); return; }
    if (!user) { navigate("/login"); return; }
    sessionStorage.setItem("selectedMarketId", String(market.id));
    const selected = markets.find((m) => String(m.id) === String(market.id));
    if ((selected?.market_type || "normal") === "starline") { navigate(`/starline/${market.id}`); return; }
    navigate("/game-select");
  };

  const openWhatsapp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello Play Online Satta Matka support");
    if (!url) return;
    openExternalLink(url);
  };

  const waPhone = String(settings.whatsapp_number || "").trim();
  const tickerText = String(
    settings.home_running_notice || "Matka kabhi bhi aapka OTP nahi mangta. — Play Online Satta Matka | Fast Results | Secure Wallet | Auto Deposit | Auto Withdraw"
  ).trim();

  return (
    <div style={page} {...pullToRefresh.bind}>

      {/* Sticky header */}
      <div style={stickyHeader}>
      <header style={topbar}>
        <button type="button" style={menuBtn} onClick={() => setOpen(true)}>
          <Menu size={22} color="#fff" />
        </button>

        <button type="button" style={brandBtn} onClick={() => navigate("/dashboard")}>
          <span style={brandText}>
            <span style={brandOrange}>Play Online</span>
            <span style={brandGreen}> Satta Matka</span>
          </span>
        </button>

        <div style={headerRight}>
          {user ? (
            <button type="button" style={walletBtn} onClick={() => navigate("/wallet")}>
              <span style={walletIcon}>₹</span>
              <div style={walletMeta}>
                <span style={walletLabel}>Balance</span>
                <span style={walletAmt}>{Number(balance).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
              </div>
              <TrendingUp size={13} color="rgba(255,255,255,0.6)" />
            </button>
          ) : (
            <button type="button" style={loginBtn} onClick={() => navigate("/login")}>Login</button>
          )}
        </div>
      </header>
      </div>{/* end stickyHeader */}

      {/* Sidebar rendered outside stickyHeader so its zIndex is global, not limited by header stacking context */}
      <Sidebar isOpen={open} setIsOpen={setOpen} />

      <div style={headerSpacer} />{/* compensate for fixed header height */}

      {/* Page notice toast */}
      {pageNotice ? <div style={noticeToast}>{pageNotice}</div> : null}

      <main style={main}>

        {/* Banner carousel */}
        <div style={bannerWrap}>
          <BannerCarousel
            banners={displayBanners}
            activeIndex={activeBanner}
            desktop={false}
            laptop={false}
            imageStyle={bannerImg}
            desktopImageStyle={bannerImg}
            laptopImageStyle={bannerImg}
            altPrefix="Banner"
            fallback={
              <div style={bannerFallback}>
                <strong style={{ fontSize: "20px", fontWeight: 900 }}>Play Online Satta Matka</strong>
                <span style={{ fontSize: "13px", marginTop: "6px", opacity: 0.8 }}>Live Markets | Fast Results | Secure Play</span>
              </div>
            }
          />
        </div>

        {/* Quick action pills */}
        <div style={quickPills}>
          <button type="button" style={quickPill} onClick={() => navigate(user ? "/starline" : "/login")}>
            <span style={pillPlay}>▶</span> Starline
          </button>
          <button type="button" style={quickPill} onClick={() => navigate("/charts")}>
            <span style={pillPlay}>▶</span> Charts
          </button>
        </div>

        {/* Deposit / Withdraw quick buttons */}
        <div style={quickPills}>
          <button type="button" style={depositPill} onClick={() => navigate(user ? "/deposit" : "/login")}>
            <span style={pillIcon}>💰</span> Deposit
          </button>
          <button type="button" style={withdrawPill} onClick={() => navigate(user ? "/withdraw" : "/login")}>
            <span style={pillIcon}>💸</span> Withdraw
          </button>
        </div>

        {/* Casino cards row */}
        {(() => {
          const showCasino = settings.show_casino_card !== "0";
          return (
            <div style={{ ...casinoRow, gridTemplateColumns: showCasino ? "1fr 1fr" : "1fr" }}>
              <button type="button" style={aviatorCard} onClick={() => navigate(user ? "/aviator" : "/login")}>
                <div style={aviatorBadge}>✈</div>
                <div style={aviatorCardTitle}>AVIATOR</div>
                <div style={aviatorCardSub}>Crash Game</div>
                <div style={aviatorCta}>PLAY →</div>
              </button>
              {showCasino && (
                <button type="button" style={casinoCard} onClick={() => navigate(user ? "/games" : "/login")}>
                  <div style={casinoBadge}>🎰</div>
                  <div style={casinoCardTitle}>CASINO</div>
                  <div style={casinoCardSub}>Live Games</div>
                  <div style={casinoCta}>PLAY →</div>
                </button>
              )}
            </div>
          );
        })()}

        {/* WhatsApp support card */}
        {waPhone ? (
          <button type="button" style={waCard} onClick={openWhatsapp}>
            <div style={waCardLeft}>
              <span style={waCardIcon}><WhatsAppGlyph size={20} color="#fff" /></span>
              <div style={waCardText}>
                <span style={waCardTitle}>WhatsApp Support</span>
                <span style={waCardSub}>+91 {String(waPhone).slice(-10)}</span>
              </div>
            </div>
            <span style={waCardArrow}>→</span>
          </button>
        ) : null}

        {/* Market list */}
        <div style={marketList}>
          {displayMarkets.map((market) => {
            const closed = isMarketClosed(market);
            const win = getMarketWindowForNow(market);
            const statusLabel = getMarketStatusLabel(market);
            const result = market.result || "*** - ** - ***";

            return (
              <article key={market.id} style={marketCard}>
                {/* Row 1: name + result */}
                <div style={cardRow1}>
                  <strong style={mktName}>{market.name}</strong>
                  <span style={mktResult}>{result}</span>
                </div>

                {/* Row 2: status + Play button */}
                <div style={cardRow2}>
                  <span style={closed ? closedStatus : openStatus}>{statusLabel}</span>
                  <button
                    type="button"
                    style={closed ? disabledBetBtn : playBtn}
                    onClick={() => openGameSelect(market)}
                    disabled={closed}
                  >
                    {closed ? "Closed" : "▶ Play"}
                  </button>
                </div>

                {/* Row 3: times + Chart button */}
                <div style={cardRow3}>
                  <div style={timeTags}>
                    <span style={timeTag}>Open {formatTime(win.open_time || market.open_time)}</span>
                    <span style={timeTag}>Close {formatTime(win.close_time || market.close_time)}</span>
                  </div>
                  <button
                    type="button"
                    style={chartBtn}
                    onClick={() => setChartPopup(market)}
                  >
                    📊 Chart
                  </button>
                </div>
              </article>
            );
          })}
        </div>

      </main>

      {/* Chart popup modal */}
      {chartPopup && (
        <div style={chartOverlay} onClick={() => setChartPopup(null)}>
          <div style={chartSheet} onClick={(e) => e.stopPropagation()}>
            <div style={chartSheetHandle} />
            <div style={chartSheetTitle}>📊 {chartPopup.name}</div>
            <div style={chartSheetSubtitle}>Select chart type to view</div>
            <div style={chartBtnRow}>
              <button type="button" style={chartOptionBtn} onClick={() => { navigate(`/charts/market/${chartPopup.id}?view=jodi`); setChartPopup(null); }}>
                <span style={chartOptionIcon}>🔢</span>
                <span style={chartOptionLabel}>Jodi Chart</span>
              </button>
              <button type="button" style={{ ...chartOptionBtn, ...chartOptionBtnAlt }} onClick={() => { navigate(`/charts/market/${chartPopup.id}?view=panel`); setChartPopup(null); }}>
                <span style={chartOptionIcon}>📋</span>
                <span style={chartOptionLabel}>Panel Chart</span>
              </button>
            </div>
            <button type="button" style={chartCloseBtn} onClick={() => setChartPopup(null)}>Close</button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

/* ── helpers ── */
function getWhatsappPhone(value) {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith("91")) return d;
  return d;
}

const sortMarketsByOpenTime = (a, b) => {
  const ra = getMarketSortRank(a), rb = getMarketSortRank(b);
  if (ra !== rb) return ra - rb;
  return toMins(a.open_time) - toMins(b.open_time);
};
const getMarketSortRank = (m) => {
  if (Number(m?.is_active ?? 1) === 0) return 2;
  return isMarketClosed(m) ? 1 : 0;
};
const toMins = (v) => {
  const x = String(v || "").match(/(\d{1,2}):(\d{2})/);
  return x ? Number(x[1]) * 60 + Number(x[2]) : Number.MAX_SAFE_INTEGER;
};
const formatTime = (v) => {
  if (!v) return "--:--";
  const m = String(v).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(v);
  const h = Number(m[1]);
  const suf = h >= 12 ? "PM" : "AM";
  return `${String(((h + 11) % 12) + 1).padStart(2, "0")}:${m[2]} ${suf}`;
};

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT", open_time: "19:05", close_time: "20:05", result: "*** - ** - ***" },
  { id: 2, name: "MADHUR NIGHT", open_time: "20:20", close_time: "22:20", result: "*** - ** - ***" },
  { id: 3, name: "MILAN NIGHT",  open_time: "20:50", close_time: "22:50", result: "*** - ** - ***" },
];

/* ── styles ── */
const PURPLE = "#1e1b4b";
const PURPLE_MID = "#312e81";
const ORANGE = "#f97316";
const GREEN = "#16a34a";

const page = {
  minHeight: "100vh",
  background: "#f1f5f9",
  paddingBottom: "72px",
  fontFamily: "Inter, system-ui, sans-serif",
  color: "#0f172a",
};

const topPromoBar = {
  background: ORANGE,
  color: "#fff",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 700,
  padding: "7px 12px",
  lineHeight: 1.4,
};
const promoLink = {
  background: "transparent",
  border: "none",
  color: "#fff",
  fontWeight: 900,
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: "12px",
  padding: 0,
};

const stickyHeader = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 100,
};
const headerSpacer = { height: 58 };

const topbar = {
  height: "58px",
  background: `linear-gradient(135deg, ${PURPLE} 0%, #2d2a6e 100%)`,
  display: "flex",
  alignItems: "center",
  padding: "0 14px",
  gap: "10px",
  boxShadow: "0 3px 16px rgba(30,27,75,0.45)",
};
const menuBtn = {
  border: 0,
  background: "rgba(255,255,255,0.1)",
  borderRadius: 10,
  width: 38, height: 38,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  cursor: "pointer",
};
const brandBtn = {
  border: 0,
  background: "transparent",
  flex: 1,
  textAlign: "left",
  cursor: "pointer",
  padding: 0,
};
const brandText = { fontWeight: 900, fontSize: "17px", letterSpacing: "-0.01em" };
const brandOrange = { color: ORANGE };
const brandGreen  = { color: "#4ade80" };
const headerRight = { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 };
const walletBtn = {
  display: "flex", alignItems: "center", gap: 7,
  background: "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.07))",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 12,
  padding: "5px 10px 5px 6px",
  cursor: "pointer",
};
const walletIcon = {
  width: 30, height: 30, borderRadius: 8,
  background: "linear-gradient(135deg, #f97316, #ea580c)",
  color: "#fff", fontSize: 15, fontWeight: 900,
  display: "grid", placeItems: "center", flexShrink: 0,
};
const walletMeta = { display: "flex", flexDirection: "column", gap: 1 };
const walletLabel = { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.06em" };
const walletAmt = { fontSize: 14, fontWeight: 900, color: "#fff", lineHeight: 1 };
const loginBtn = {
  border: 0,
  borderRadius: "999px",
  background: ORANGE,
  color: "#fff",
  padding: "5px 14px",
  fontWeight: 800,
  fontSize: "13px",
  cursor: "pointer",
};

const tickerBar = {
  background: PURPLE_MID,
  overflow: "hidden",
  minHeight: "32px",
};
const tickerOuter = { width: "100%", minHeight: "32px", overflow: "hidden" };
const tickerTrack = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform" };
const tickerText2 = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "32px",
  padding: "0 20px",
  color: "#e0e7ff",
  fontSize: "12px",
  fontWeight: 700,
  flexShrink: 0,
};

const noticeToast = {
  position: "fixed",
  top: "96px",
  left: "50%",
  transform: "translateX(-50%)",
  background: "#fff",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  borderRadius: "12px",
  padding: "10px 18px",
  fontSize: "13px",
  fontWeight: 800,
  zIndex: 60,
  boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  whiteSpace: "nowrap",
};

const main = { padding: "12px 12px 24px", display: "grid", gap: "12px", maxWidth: "600px", margin: "0 auto" };

const bannerWrap = { borderRadius: "14px", overflow: "hidden", background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" };
const bannerImg = { width: "100%", height: "clamp(160px, 42vw, 210px)", objectFit: "cover", display: "block" };
const bannerFallback = {
  height: "190px",
  background: `linear-gradient(135deg, ${PURPLE}, #4f46e5)`,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

const quickPills = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" };

// Casino cards row
const casinoRow      = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
// Aviator card
const aviatorCard    = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, background: "linear-gradient(145deg, #0d0d22 0%, #1e1b4b 60%, #312e81 100%)", border: "1.5px solid rgba(124,58,237,0.45)", borderRadius: 16, padding: "18px 10px", cursor: "pointer", boxShadow: "0 6px 20px rgba(30,27,75,0.5)", textAlign: "center", minHeight: 110 };
const aviatorBadge   = { fontSize: 28, lineHeight: 1 };
const aviatorCardTitle = { fontSize: 15, fontWeight: 900, color: "#facc15", letterSpacing: "0.08em" };
const aviatorCardSub = { fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 600 };
const aviatorCta     = { fontSize: 12, fontWeight: 900, color: "#a78bfa", marginTop: 2 };
// Casino card
const casinoCard     = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, background: "linear-gradient(145deg, #0a1a0a 0%, #0f3a0f 60%, #14532d 100%)", border: "1.5px solid rgba(22,163,74,0.45)", borderRadius: 16, padding: "18px 10px", cursor: "pointer", boxShadow: "0 6px 20px rgba(20,83,45,0.4)", textAlign: "center", minHeight: 110 };
const casinoBadge    = { fontSize: 28, lineHeight: 1 };
const casinoCardTitle= { fontSize: 15, fontWeight: 900, color: "#4ade80", letterSpacing: "0.06em" };
const casinoCardSub  = { fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 600 };
const casinoCta      = { fontSize: 12, fontWeight: 900, color: "#86efac", marginTop: 2 };

const quickPill = {
  height: "44px",
  border: 0,
  borderRadius: "999px",
  background: PURPLE,
  color: "#fff",
  fontSize: "12px",
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(30,27,75,0.22)",
};
const quickPillCenter = { background: "#312e81" };
const pillPlay = { fontSize: "11px", opacity: 0.85 };
const pillIcon = { fontSize: "14px" };

const waCard = { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", border: 0, borderRadius: 14, background: "linear-gradient(135deg, #128C7E, #25D366)", padding: "12px 14px", cursor: "pointer", boxShadow: "0 4px 14px rgba(37,211,102,0.3)" };
const waCardLeft = { display: "flex", alignItems: "center", gap: 10 };
const waCardIcon = { width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "grid", placeItems: "center", flexShrink: 0 };
const waCardText = { display: "flex", flexDirection: "column", gap: 2, textAlign: "left" };
const waCardTitle = { color: "#fff", fontWeight: 900, fontSize: 14 };
const waCardSub = { color: "rgba(255,255,255,0.8)", fontSize: 11, fontWeight: 600 };
const waCardArrow = { color: "rgba(255,255,255,0.7)", fontSize: 18, fontWeight: 900 };

const marketList = { display: "grid", gap: "10px" };
const marketCard = {
  background: "#fff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  padding: "12px 14px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  display: "grid",
  gap: "8px",
};
const cardRow1 = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" };
const mktName = { fontSize: "16px", fontWeight: 900, color: "#0f172a", textTransform: "uppercase" };
const mktResult = { fontSize: "16px", fontWeight: 900, color: PURPLE, flexShrink: 0 };

const cardRow2 = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const openStatus = { fontSize: "12px", fontWeight: 700, color: GREEN };
const closedStatus = { fontSize: "12px", fontWeight: 700, color: "#ef4444" };
const cameraBtn = { width: 38, height: 38, border: 0, borderRadius: "50%", background: "linear-gradient(135deg, #4f46e5, #7c3aed)", cursor: "pointer", display: "grid", placeItems: "center", boxShadow: "0 2px 10px rgba(79,70,229,0.4)", flexShrink: 0 };
const chartBallInner = { fontSize: 18, lineHeight: 1 };
const chartOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" };
const chartSheet = { width: "100%", maxWidth: 540, margin: "0 auto", background: "#fff", borderRadius: "24px 24px 0 0", padding: "12px 16px 32px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 };
const chartSheetHandle = { width: 40, height: 4, borderRadius: 999, background: "#e5e7eb", marginBottom: 4 };
const chartSheetTitle = { fontSize: 18, fontWeight: 800, color: "#111827" };
const chartSheetSubtitle = { fontSize: 13, color: "#6b7280" };
const chartBtnRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: "100%", marginTop: 4 };
const chartOptionBtn = { padding: "18px 12px", border: "2px solid #e5e7eb", borderRadius: 16, background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer" };
const chartOptionBtnAlt = { background: "#faf5ff", borderColor: "#ddd6fe" };
const chartOptionIcon = { fontSize: 28 };
const chartOptionLabel = { fontSize: 13, fontWeight: 800, color: "#1e293b" };
const chartCloseBtn = { marginTop: 4, padding: "10px 32px", border: "1px solid #e5e7eb", borderRadius: 999, background: "#fff", color: "#6b7280", fontWeight: 700, fontSize: 14, cursor: "pointer" };

const cardRow3 = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" };
const timeTags = { display: "flex", gap: "6px", flexWrap: "wrap" };
const timeTag = {
  background: "#f1f5f9",
  color: "#475569",
  fontSize: "11px",
  fontWeight: 700,
  padding: "3px 8px",
  borderRadius: "6px",
  whiteSpace: "nowrap",
};
const playBtn = {
  minWidth: "76px",
  height: "34px",
  border: 0,
  borderRadius: "8px",
  background: `linear-gradient(135deg, ${GREEN}, #15803d)`,
  color: "#fff",
  fontWeight: 900,
  fontSize: "13px",
  cursor: "pointer",
  flexShrink: 0,
  boxShadow: "0 2px 8px rgba(22,163,74,0.35)",
  letterSpacing: "0.02em",
};
const disabledBetBtn = {
  ...playBtn,
  background: "#e2e8f0",
  color: "#94a3b8",
  boxShadow: "none",
  cursor: "not-allowed",
};
const chartBtn = {
  minWidth: "76px",
  height: "30px",
  border: "1.5px solid #c7d2fe",
  borderRadius: "8px",
  background: "#eef2ff",
  color: "#4338ca",
  fontWeight: 800,
  fontSize: "12px",
  cursor: "pointer",
  flexShrink: 0,
};

const depositPill = {
  ...quickPill,
  background: "linear-gradient(135deg, #15803d, #16a34a)",
  boxShadow: "0 4px 12px rgba(22,163,74,0.28)",
};
const withdrawPill = {
  ...quickPill,
  background: "linear-gradient(135deg, #b45309, #d97706)",
  boxShadow: "0 4px 12px rgba(180,83,9,0.28)",
};

export default Dashboard;
